import {
  type Dispatch,
  type ForwardedRef,
  type HTMLAttributes,
  type MouseEvent,
  type ReactNode,
  type SetStateAction,
  cloneElement,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useMeasure } from 'react-use';
import styled from '@emotion/styled';
import { layers } from '__app/spacing';
import Icon from '__components/Kizen/Icon';
import IconButton, { ICON_BUTTON_SIZING } from '__components/Kizen/IconButton';
import { Gradient } from '__components/Kizen/ScrollFadeContainer';

export type ExpandState = 'open' | 'partially-open' | 'close';

export type ExpanderHandle = {
  toggle(): void;
  set: Dispatch<SetStateAction<ExpandState>>;
};

type ExpanderProps = Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> & {
  arrowBtnLayerFudge?: number;
  children: ReactNode;
  closedState?: Exclude<ExpandState, 'open'>;
  gradient?: JSX.Element;
  initialState?: ExpandState;
  partiallyOpenHeight?: number;
  showArrowBtn?: boolean;
  title?: string;
  onChange?(state: ExpandState): void;
  onExpanderHover?(): void;
};

const Body = styled.div`
  transition: height 0.3s ease-in-out;
  overflow: hidden;
`;

const Wrapper = styled.div`
  overflow: hidden;
`;

const ExpansionWrapper = styled.div<{ fudge?: number }>`
  position: absolute;
  width: 100%;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: ${({ fudge }) => layers.content(0, fudge)};
`;

const isClosed = (expanded: ExpandState) => {
  return ['close', 'partially-open'].includes(expanded);
};

const ExpansionButton = styled(({ expanded, ...others }) => {
  const { t } = useTranslation();
  return (
    <IconButton
      title={isClosed(expanded) ? t('Open') : t('Close')}
      sizing={ICON_BUTTON_SIZING.overlay}
      {...others}
    >
      {isClosed(expanded) && <Icon icon="arrow-from-top" />}
      {expanded === 'open' && <Icon icon="arrow-from-bottom" />}
    </IconButton>
  );
})`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translate(-50%, 50%);
`;

const Expander = forwardRef(
  (props: ExpanderProps, ref: ForwardedRef<ExpanderHandle>) => {
    const {
      closedState = 'close',
      initialState,
      onChange,
      children,
      gradient = <Gradient />,
      partiallyOpenHeight = 70,
      showArrowBtn = true,
      arrowBtnLayerFudge = 2,
      title = '',
      onExpanderHover,
      ...others
    } = props;
    const bodyRef = useRef(null);
    const timerRef = useRef<NodeJS.Timeout>();
    const [expansionRef, { height: openHeight = 0 }] =
      useMeasure<HTMLDivElement>();
    const [expandedState, setExpandedState] = useState(
      initialState ?? closedState
    );

    const getHeight = () => {
      if (expandedState === 'open') {
        return openHeight;
      }

      if (expandedState === 'partially-open') {
        return partiallyOpenHeight;
      }

      return 0;
    };

    const handle = useMemo(() => {
      const toggle = () => {
        setExpandedState((prev) => {
          const next = prev === 'open' ? closedState : 'open';

          if (onChange) {
            if (next === 'open') {
              onChange(next);
            } else {
              timerRef.current = setTimeout(() => onChange(next), 300);
            }
          }

          return next;
        });
      };

      return { set: setExpandedState, toggle };
    }, [closedState, onChange]);

    useImperativeHandle(
      ref,
      () => {
        return handle;
      },
      [handle]
    );

    useEffect(() => {
      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
      };
    }, []);

    return (
      <>
        <Wrapper {...others}>
          <Body ref={bodyRef} style={{ height: getHeight() }}>
            <div ref={expansionRef}>{children}</div>
          </Body>
          {gradient &&
            cloneElement(gradient, {
              position: 'bottom',
              fadeIn: isClosed(expandedState),
            })}
        </Wrapper>
        {showArrowBtn && (
          // You must set position: relative; on whichever ancestor element along
          // whose width you want this button to be centered, as ExpansionWrapper is absolute positioned
          <ExpansionWrapper
            fudge={arrowBtnLayerFudge}
            onMouseOver={onExpanderHover}
          >
            <ExpansionButton
              data-qa={`${title}_expansion_button`}
              expanded={expandedState}
              onClick={handle.toggle}
              onMouseDown={(ev: MouseEvent) => {
                // Prevent focus so that the browser does not try to keep
                // this button element in the viewport when the page height changes.
                ev.preventDefault();
              }}
            />
          </ExpansionWrapper>
        )}
      </>
    );
  }
);

export default Expander;
